import { clearRememberMeCookie } from './cookies';

const BASE = (
  process.env.REACT_APP_EZTS_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_EZTS_API ||
  ''
).replace(/\/$/, '');

const joinUrl = (base, path) => {
  const b = String(base || '').replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  if (!b) return `/${p}`;
  return `${b}/${p}`;
};

const parseResponseBody = async (res) => {
  // Prefer JSON parse even when Content-Type is wrong.
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
};

function isSameOriginApi() {
  if (typeof window === 'undefined') return false;
  if (!BASE) return true;
  try {
    const url = /^https?:\/\//i.test(BASE)
      ? new URL(BASE)
      : new URL(BASE, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return true;
  }
}

const httpJson = async (method, path, { body, headers, credentials = 'include' } = {}) => {
  if (!BASE) {
    throw new Error(
      'API base URL not configured (set REACT_APP_EZTS_API_BASE_URL or REACT_APP_API_BASE_URL)'
    );
  }

  const url = joinUrl(BASE, path);
  let res;
  try {
    res = await fetch(url, {
      method,
      credentials,
      headers: {
        'Content-Type': 'application/json',
        ...(headers || {}),
      },
      body: body == null ? undefined : JSON.stringify(body),
    });
  } catch (networkErr) {
    const err = new Error(
      networkErr?.message === 'Failed to fetch'
        ? 'Network error — could not reach the API. Check VPN/server and that the API URL is correct.'
        : networkErr?.message || 'Network error'
    );
    err.response = { status: 0, data: { message: err.message } };
    err.cause = networkErr;
    throw err;
  }

  const data = await parseResponseBody(res);
  const responseHeaders = {};
  try {
    res.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
  } catch {
    // ignore
  }

  if (!res.ok) {
    const err = new Error((data && data.message) || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }
  return {
    data,
    status: res.status,
    statusText: res.statusText,
    headers: responseHeaders,
    config: {},
  };
};

function unwrapAuthPayload(res) {
  return res?.data?.data || res?.data || {};
}

/**
 * POST /ezts/v2/auth/login
 * Body: { email, password, rememberMe }
 *
 * Same-origin (CRA `/ezts` proxy): credentials include so BE can Set-Cookie
 * the HttpOnly refresh token. Cross-origin BE with ACAO:* cannot accept
 * credentialed login — cookie refresh will not work until CORS is fixed.
 */
export async function login({ email, password, username, rememberMe = false }) {
  const rm = Boolean(rememberMe);

  const body = username
    ? { username, password, rememberMe: rm }
    : { email, password, rememberMe: rm };

  return httpJson('POST', 'v2/auth/login', {
    body,
    credentials: isSameOriginApi() ? 'include' : 'omit',
  });
}

/**
 * POST /ezts/v2/auth/refresh?uid=
 */
export async function refreshSession({ uid } = {}) {
  const qs = uid != null && uid !== '' ? `?uid=${encodeURIComponent(String(uid))}` : '';
  return httpJson('POST', `v2/auth/refresh${qs}`, { body: {}, credentials: 'include' });
}

/**
 * POST /ezts/v2/auth/logout?uid=
 */
export async function logoutSession({ uid } = {}) {
  try {
    const qs = uid != null && uid !== '' ? `?uid=${encodeURIComponent(String(uid))}` : '';
    return await httpJson('POST', `v2/auth/logout${qs}`, { body: {}, credentials: 'include' });
  } catch (err) {
    clearRememberMeCookie();
    throw err;
  }
}

export function extractLoginResult(res) {
  const data = unwrapAuthPayload(res);
  const headerToken =
    res?.headers?.['x-access-token'] ||
    res?.headers?.['X-Access-Token'] ||
    null;
  const accessToken = data?.token || data?.accessToken || data?.jwt || headerToken;
  let user = data?.user || data?.userDto || data?.profile || null;

  if (!user && data && typeof data === 'object') {
    const { token: _t, accessToken: _at, jwt: _jwt, refreshToken: _rt, rememberMe: _rm, ...rest } =
      data;
    if (Object.keys(rest).length > 0) user = rest;
  }

  return { accessToken, user };
}

export function extractAccessToken(res) {
  const data = unwrapAuthPayload(res);
  return (
    data?.token ||
    data?.accessToken ||
    data?.jwt ||
    res?.headers?.['x-access-token'] ||
    res?.headers?.['X-Access-Token'] ||
    null
  );
}

function isLikelyJwt(value) {
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (s.length < 20) return false;
  const parts = s.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

function headerAccessToken(res) {
  const headers = res?.headers;
  if (!headers) return null;
  const raw =
    headers['x-access-token'] ||
    headers['X-Access-Token'] ||
    (typeof headers.get === 'function' ? headers.get('x-access-token') : null);
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  return v || null;
}

function pickAccessTokenFromBag(bag) {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return null;
  const direct = bag.accessToken || bag.jwt;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  if (isLikelyJwt(bag.token)) return bag.token.trim();
  return null;
}

/**
 * Token rotation on business API responses (envelope or x-access-token header).
 * Ignores unrelated `token` fields that are not JWTs.
 */
export function extractRotatedAccessToken(res) {
  const fromHeader = headerAccessToken(res);
  if (fromHeader) return fromHeader;

  const root = res?.data;
  const inner = root && typeof root === 'object' && !Array.isArray(root) ? root.data : null;
  return pickAccessTokenFromBag(root) || pickAccessTokenFromBag(inner);
}
